import { FormEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { AdminCompanyCreateResponse } from '../../services/api';
import { useAdminCompanyMutations } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { Button } from '../ui/button';
import { formFieldGroupSurfaceClassName } from '../ui/formPrimitives';
import { Input } from '../ui/input';
import {
    mergeCompanyUserlyticsSettings,
    readCompanyUserlyticsSettings,
    type CompanyUserlyticsSettings,
} from './companyUserlyticsCustomizations';

const DEFAULT_INDUSTRY_ID = 1;
const EMPTY_USERLYTICS_SETTINGS: CompanyUserlyticsSettings = {
    enabled: false,
    email: '',
    domain: '',
    teamId: '',
    projectId: '',
};

interface AdminCreateCompanyModalProps {
    open: boolean;
    onClose: () => void;
    onCreated?: (result: AdminCompanyCreateResponse) => void;
}

interface AdminEditCompanyModalProps {
    company: {
        companyId: number;
        name: string;
        isActive: boolean;
        customizations?: Record<string, unknown>;
    } | null;
    open: boolean;
    onClose: () => void;
    onSaved?: (result: AdminCompanyCreateResponse) => void;
}

interface CompanyFormModalProps {
    company?: AdminEditCompanyModalProps['company'];
    mode: 'create' | 'edit';
    open: boolean;
    onClose: () => void;
    onSubmitted?: (result: AdminCompanyCreateResponse) => void;
}

function getCompanyCustomizations(
    baseCustomizations: Record<string, unknown>,
    hasPulseAccess: boolean,
    hasProAccess: boolean,
    userlytics: CompanyUserlyticsSettings,
): Record<string, unknown> {
    const customizations = mergeCompanyUserlyticsSettings(baseCustomizations, userlytics);

    if (hasPulseAccess) {
        const automatedInsights = customizations.automatedInsights;
        if (
            !automatedInsights ||
            typeof automatedInsights !== 'object' ||
            !('allowedUserIds' in automatedInsights)
        ) {
            delete customizations.automatedInsights;
        }
    } else {
        customizations.automatedInsights = { enabled: false };
    }

    if (hasProAccess) {
        delete customizations.core;
    } else {
        customizations.core = { enabled: false };
    }

    return customizations;
}

function derivePulseAccess(customizations: Record<string, unknown>): boolean {
    const automatedInsights = customizations.automatedInsights as { enabled?: boolean } | undefined;
    return automatedInsights?.enabled === true || !('automatedInsights' in customizations);
}

function deriveProAccess(customizations: Record<string, unknown>): boolean {
    const core = customizations.core as { enabled?: boolean } | undefined;
    return core?.enabled === true || !('core' in customizations);
}

export function AdminCreateCompanyModal({
    open,
    onClose,
    onCreated,
}: AdminCreateCompanyModalProps) {
    return <CompanyFormModal mode="create" open={open} onClose={onClose} onSubmitted={onCreated} />;
}

export function AdminEditCompanyModal({
    company,
    open,
    onClose,
    onSaved,
}: AdminEditCompanyModalProps) {
    return (
        <CompanyFormModal
            company={company}
            mode="edit"
            open={open}
            onClose={onClose}
            onSubmitted={onSaved}
        />
    );
}

function CompanyFormModal({ company, mode, open, onClose, onSubmitted }: CompanyFormModalProps) {
    const { createCompany, updateCompany } = useAdminCompanyMutations();
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [hasPulseAccess, setHasPulseAccess] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [userlytics, setUserlytics] =
        useState<CompanyUserlyticsSettings>(EMPTY_USERLYTICS_SETTINGS);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        const customizations = company?.customizations ?? {};
        setName(company?.name ?? '');
        setIsActive(company?.isActive ?? true);
        setHasPulseAccess(company ? derivePulseAccess(customizations) : false);
        setHasProAccess(company ? deriveProAccess(customizations) : false);
        setUserlytics(readCompanyUserlyticsSettings(customizations));
        setError(null);
    }, [company, open]);

    if (!open) return null;

    const isEdit = mode === 'edit';
    const isPending = isEdit ? updateCompany.isPending : createCompany.isPending;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Company name is required.');
            return;
        }

        const normalizedUserlytics = {
            ...userlytics,
            email: userlytics.email.trim(),
            domain: userlytics.domain.trim(),
            teamId: userlytics.teamId.trim(),
            projectId: userlytics.projectId.trim(),
        };
        if (
            normalizedUserlytics.enabled &&
            (!normalizedUserlytics.email ||
                !normalizedUserlytics.domain ||
                !normalizedUserlytics.teamId ||
                !normalizedUserlytics.projectId)
        ) {
            setError('Userlytics email, domain, team ID, and project ID are required.');
            return;
        }

        try {
            const customizations = getCompanyCustomizations(
                company?.customizations ?? {},
                hasPulseAccess,
                hasProAccess,
                normalizedUserlytics,
            );
            const result =
                isEdit && company
                    ? await updateCompany.mutateAsync({
                          companyId: company.companyId,
                          request: {
                              name: trimmedName,
                              isActive,
                              customizations,
                          },
                      })
                    : await createCompany.mutateAsync({
                          name: trimmedName,
                          industryId: DEFAULT_INDUSTRY_ID,
                          isActive,
                          customizations,
                      });
            onSubmitted?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, `Failed to ${mode} company.`));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl">
                <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 className="text-xl font-semibold text-neutral-950">
                            {isEdit ? 'Edit Company' : 'Create Company'}
                        </h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            {isEdit
                                ? 'Update company profile and product access.'
                                : 'Creates a company with the selected product access.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                        <X className="size-5" />
                    </button>
                </div>

                <form
                    className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5"
                    onSubmit={handleSubmit}
                >
                    {error && (
                        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
                    )}

                    <Field label="Company name">
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Acme Research"
                        />
                    </Field>

                    <Toggle label="Active" checked={isActive} onChange={setIsActive} />
                    <Toggle
                        label="Pulse access"
                        checked={hasPulseAccess}
                        onChange={setHasPulseAccess}
                    />
                    <Toggle label="Pro access" checked={hasProAccess} onChange={setHasProAccess} />
                    <Toggle
                        label="Userlytics access"
                        checked={userlytics.enabled}
                        onChange={(enabled) =>
                            setUserlytics((current) => ({ ...current, enabled }))
                        }
                    />

                    {userlytics.enabled ? (
                        <div className={`space-y-4 ${formFieldGroupSurfaceClassName}`}>
                            <Field label="Userlytics email">
                                <Input
                                    type="email"
                                    value={userlytics.email}
                                    onChange={(event) =>
                                        setUserlytics((current) => ({
                                            ...current,
                                            email: event.target.value,
                                        }))
                                    }
                                    placeholder="research@example.com"
                                />
                            </Field>
                            <Field label="Userlytics domain">
                                <Input
                                    value={userlytics.domain}
                                    onChange={(event) =>
                                        setUserlytics((current) => ({
                                            ...current,
                                            domain: event.target.value,
                                        }))
                                    }
                                    placeholder="example.wevo.ai"
                                />
                            </Field>
                            <Field label="Userlytics team ID">
                                <Input
                                    value={userlytics.teamId}
                                    onChange={(event) =>
                                        setUserlytics((current) => ({
                                            ...current,
                                            teamId: event.target.value,
                                        }))
                                    }
                                    placeholder="7tv40kuw"
                                />
                            </Field>
                            <Field label="Userlytics project ID">
                                <Input
                                    value={userlytics.projectId}
                                    onChange={(event) =>
                                        setUserlytics((current) => ({
                                            ...current,
                                            projectId: event.target.value,
                                        }))
                                    }
                                    placeholder="p12dwkeh"
                                />
                            </Field>
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending
                                ? isEdit
                                    ? 'Saving...'
                                    : 'Creating...'
                                : isEdit
                                  ? 'Save Company'
                                  : 'Create Company'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ children, label }: { children: JSX.Element; label: string }) {
    return (
        <label className="block space-y-1">
            <span className="text-sm font-medium text-neutral-800">{label}</span>
            {children}
        </label>
    );
}

function Toggle({
    checked,
    label,
    onChange,
}: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-center gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="size-4 rounded border-neutral-300"
            />
            <span className="text-sm font-semibold text-neutral-950">{label}</span>
        </label>
    );
}
